import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoadingStatus } from "../sessions/models/LoadingStatus";
import { ApiErrorResponse } from "../../api/types/ApiErrorResponse";
import { RootState } from "../../app/store";
import { AdminUser } from "./models/AdminUser";
import { AdminFeedbackEntry } from "./models/AdminFeedbackEntry";
import { ReferralPartner } from "./models/ReferralPartner";
import { AdminAcquisition } from "./models/AdminAcquisition";
import { AdminUserMembers } from "./models/AdminUserMembers";
import { fetchAdminUserMembers } from "./slice-extensions/admin-user-members-slice-extension";
import { AdminUserActivity } from "./models/AdminUserActivity";
import { fetchAdminUserActivity } from "./slice-extensions/admin-user-activity-slice-extension";
import {
  changeUserLoginEmail,
  createReferralPartner,
  deleteReferralPartner,
  fetchAdminAcquisition,
  fetchAdminUsers,
  fetchAdminFeedback,
  fetchReferralPartnerDetail,
  fetchReferralPartners,
  markReferralPartnerPaid,
  setUserComplimentary,
  setUserCustomPrice,
  CustomPriceUpdate,
  PaginatedAdminUsers,
  PaginatedAdminFeedback,
  ComplimentaryUpdate,
  PaginatedReferralPartners,
  ReferralPartnerDetail,
} from "./slice-extensions/admin-slice-extension";
import { ChangeUserLoginEmailResponse } from "./contracts/ChangeUserLoginEmailResponse";

interface AdminState {
  users: AdminUser[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  search: string;
  status: LoadingStatus;
  lastError?: ApiErrorResponse;
  feedback: AdminFeedbackEntry[];
  feedbackPage: number;
  feedbackTotalCount: number;
  feedbackTotalPages: number;
  feedbackStatus: LoadingStatus;
  referralPartners: ReferralPartner[];
  referralPartnersPage: number;
  referralPartnersTotalCount: number;
  referralPartnersTotalPages: number;
  referralPartnersStatus: LoadingStatus;
  referralPartnerDetail: ReferralPartnerDetail | null;
  referralPartnerDetailStatus: LoadingStatus;
  acquisition: AdminAcquisition | null;
  acquisitionStatus: LoadingStatus;
  userMembers: AdminUserMembers | null;
  userMembersStatus: LoadingStatus;
  userActivity: AdminUserActivity | null;
  userActivityStatus: LoadingStatus;
}

const initialState: AdminState = {
  users: [],
  page: 1,
  pageSize: 20,
  totalCount: 0,
  totalPages: 0,
  search: "",
  status: LoadingStatus.Idle,
  lastError: undefined,
  feedback: [],
  feedbackPage: 1,
  feedbackTotalCount: 0,
  feedbackTotalPages: 0,
  feedbackStatus: LoadingStatus.Idle,
  referralPartners: [],
  referralPartnersPage: 1,
  referralPartnersTotalCount: 0,
  referralPartnersTotalPages: 0,
  referralPartnersStatus: LoadingStatus.Idle,
  referralPartnerDetail: null,
  referralPartnerDetailStatus: LoadingStatus.Idle,
  acquisition: null,
  acquisitionStatus: LoadingStatus.Idle,
  userMembers: null,
  userMembersStatus: LoadingStatus.Idle,
  userActivity: null,
  userActivityStatus: LoadingStatus.Idle,
};

const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    setAdminUserSearch: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminUsers.pending, (state) => {
        state.status = LoadingStatus.Loading;
        state.lastError = undefined;
      })
      .addCase(
        fetchAdminUsers.fulfilled,
        (state, action: PayloadAction<PaginatedAdminUsers>) => {
          state.status = LoadingStatus.Idle;
          state.users = action.payload.items;
          state.page = action.payload.page;
          state.pageSize = action.payload.pageSize;
          state.totalCount = action.payload.totalCount;
          state.totalPages = action.payload.totalPages;
        }
      )
      .addCase(fetchAdminUsers.rejected, (state, action) => {
        state.status = LoadingStatus.Failed;
        state.lastError = action.payload;
      })
      .addCase(
        setUserComplimentary.fulfilled,
        (state, action: PayloadAction<ComplimentaryUpdate>) => {
          const user = state.users.find(
            (candidate) => candidate.instructorId === action.payload.instructorId
          );
          if (user) {
            user.complimentaryUntil = action.payload.complimentaryUntil;
            user.subscriptionState = action.payload.subscriptionState;
            user.hasSubscription = true;
          }
        }
      )
      .addCase(
        changeUserLoginEmail.fulfilled,
        (state, action: PayloadAction<ChangeUserLoginEmailResponse>) => {
          const user = state.users.find(
            (candidate) => candidate.instructorId === action.payload.instructorId
          );
          if (user) {
            user.email = action.payload.email;
          }
        }
      )
      .addCase(fetchAdminFeedback.pending, (state) => {
        state.feedbackStatus = LoadingStatus.Loading;
      })
      .addCase(
        fetchAdminFeedback.fulfilled,
        (state, action: PayloadAction<PaginatedAdminFeedback>) => {
          state.feedbackStatus = LoadingStatus.Idle;
          state.feedback = action.payload.items;
          state.feedbackPage = action.payload.page;
          state.feedbackTotalCount = action.payload.totalCount;
          state.feedbackTotalPages = action.payload.totalPages;
        }
      )
      .addCase(fetchAdminFeedback.rejected, (state) => {
        state.feedbackStatus = LoadingStatus.Failed;
      })
      .addCase(
        setUserCustomPrice.fulfilled,
        (state, action: PayloadAction<CustomPriceUpdate>) => {
          const user = state.users.find(
            (candidate) => candidate.instructorId === action.payload.instructorId
          );
          if (user) {
            user.customMonthlyAmountMinorUnits = action.payload.customMonthlyAmountMinorUnits;
            user.customAnnualAmountMinorUnits = action.payload.customAnnualAmountMinorUnits;
            user.customPriceCurrency = action.payload.customPriceCurrency;
            user.hasSubscription = true;
          }
        }
      )
      .addCase(fetchAdminAcquisition.pending, (state) => {
        state.acquisitionStatus = LoadingStatus.Loading;
      })
      .addCase(
        fetchAdminAcquisition.fulfilled,
        (state, action: PayloadAction<AdminAcquisition>) => {
          state.acquisitionStatus = LoadingStatus.Idle;
          state.acquisition = action.payload;
        }
      )
      .addCase(fetchAdminAcquisition.rejected, (state) => {
        state.acquisitionStatus = LoadingStatus.Failed;
      })
      .addCase(fetchAdminUserMembers.pending, (state) => {
        state.userMembersStatus = LoadingStatus.Loading;
        state.userMembers = null;
      })
      .addCase(
        fetchAdminUserMembers.fulfilled,
        (state, action: PayloadAction<AdminUserMembers>) => {
          state.userMembersStatus = LoadingStatus.Idle;
          state.userMembers = action.payload;
        }
      )
      .addCase(fetchAdminUserMembers.rejected, (state) => {
        state.userMembersStatus = LoadingStatus.Failed;
      })
      .addCase(fetchAdminUserActivity.pending, (state) => {
        state.userActivityStatus = LoadingStatus.Loading;
        state.userActivity = null;
      })
      .addCase(
        fetchAdminUserActivity.fulfilled,
        (state, action: PayloadAction<AdminUserActivity>) => {
          state.userActivityStatus = LoadingStatus.Idle;
          state.userActivity = action.payload;
        }
      )
      .addCase(fetchAdminUserActivity.rejected, (state) => {
        state.userActivityStatus = LoadingStatus.Failed;
      })
      .addCase(fetchReferralPartners.pending, (state) => {
        state.referralPartnersStatus = LoadingStatus.Loading;
      })
      .addCase(
        fetchReferralPartners.fulfilled,
        (state, action: PayloadAction<PaginatedReferralPartners>) => {
          state.referralPartnersStatus = LoadingStatus.Idle;
          state.referralPartners = action.payload.items;
          state.referralPartnersPage = action.payload.page;
          state.referralPartnersTotalCount = action.payload.totalCount;
          state.referralPartnersTotalPages = action.payload.totalPages;
        }
      )
      .addCase(fetchReferralPartners.rejected, (state) => {
        state.referralPartnersStatus = LoadingStatus.Failed;
      })
      .addCase(
        createReferralPartner.fulfilled,
        (state, action: PayloadAction<ReferralPartner>) => {
          state.referralPartners = [action.payload, ...state.referralPartners];
          state.referralPartnersTotalCount += 1;
        }
      )
      .addCase(fetchReferralPartnerDetail.pending, (state) => {
        state.referralPartnerDetailStatus = LoadingStatus.Loading;
        state.referralPartnerDetail = null;
      })
      .addCase(
        fetchReferralPartnerDetail.fulfilled,
        (state, action: PayloadAction<ReferralPartnerDetail>) => {
          state.referralPartnerDetailStatus = LoadingStatus.Idle;
          state.referralPartnerDetail = action.payload;
        }
      )
      .addCase(fetchReferralPartnerDetail.rejected, (state) => {
        state.referralPartnerDetailStatus = LoadingStatus.Failed;
      })
      .addCase(
        markReferralPartnerPaid.fulfilled,
        (state, action: PayloadAction<ReferralPartnerDetail>) => {
          state.referralPartnerDetail = action.payload;
        }
      )
      .addCase(deleteReferralPartner.fulfilled, (state, action: PayloadAction<string>) => {
        state.referralPartners = state.referralPartners.filter(
          (partner) => partner.id !== action.payload
        );
        if (state.referralPartnerDetail?.partner.id === action.payload) {
          state.referralPartnerDetail = null;
        }
      });
  },
});

export const { setAdminUserSearch } = adminSlice.actions;

export const selectAdminUsers = (state: RootState) => state.admin.users;
export const selectAdminUsersStatus = (state: RootState) => state.admin.status;
export const selectAdminUsersPage = (state: RootState) => state.admin.page;
export const selectAdminUsersTotalPages = (state: RootState) => state.admin.totalPages;
export const selectAdminUsersTotalCount = (state: RootState) => state.admin.totalCount;
export const selectAdminUserSearch = (state: RootState) => state.admin.search;
export const selectAdminUsersError = (state: RootState) => state.admin.lastError;
export const selectReferralPartners = (state: RootState) => state.admin.referralPartners;
export const selectReferralPartnersStatus = (state: RootState) =>
  state.admin.referralPartnersStatus;
export const selectReferralPartnersTotalCount = (state: RootState) =>
  state.admin.referralPartnersTotalCount;
export const selectReferralPartnersTotalPages = (state: RootState) =>
  state.admin.referralPartnersTotalPages;
export const selectAdminUserMembers = (state: RootState) => state.admin.userMembers;
export const selectAdminUserMembersStatus = (state: RootState) =>
  state.admin.userMembersStatus;
export const selectAdminUserActivity = (state: RootState) => state.admin.userActivity;
export const selectAdminUserActivityStatus = (state: RootState) =>
  state.admin.userActivityStatus;
export const selectReferralPartnerDetail = (state: RootState) =>
  state.admin.referralPartnerDetail;
export const selectReferralPartnerDetailStatus = (state: RootState) =>
  state.admin.referralPartnerDetailStatus;
export const selectAdminFeedback = (state: RootState) => state.admin.feedback;
export const selectAdminFeedbackStatus = (state: RootState) => state.admin.feedbackStatus;
export const selectAdminFeedbackTotalCount = (state: RootState) => state.admin.feedbackTotalCount;
export const selectAdminFeedbackTotalPages = (state: RootState) => state.admin.feedbackTotalPages;

export const selectAdminAcquisition = (state: RootState) => state.admin.acquisition;
export const selectAdminAcquisitionStatus = (state: RootState) => state.admin.acquisitionStatus;

export default adminSlice.reducer;
